import React, { useEffect, useState } from 'react';
import { SocialPlatformSelector } from '../components/SocialMediaPosts/SocialPlatformSelector';
import { CustomerGroupSelector } from '../components/shared/CustomerGroupSelector';
import { ArticleUrlInput } from '../components/SocialMediaPosts/ArticleUrlInput';
import { GeneratedPosts } from '../components/SocialMediaPosts/GeneratedPosts';
import { StreamingModal, StreamingProgressItem } from '../components/shared/StreamingModal';
import { useTranslation } from 'react-i18next';
import DomainSelector from '../components/shared/DomainSelector';
import { useData } from '../contexts/DataContext';
import StatusMessage from '../components/CategoryDescription/StatusMessage';
import { ErrorBoundary } from '../components/shared/ErrorBoundary';
import { useNotification } from '../contexts/NotificationContext';
import { aiService } from '../services/aiService';
import { Post } from '../components/SocialMediaPosts/types';
import { Domain } from '../types/data';
import { extractErrorMessageFromResponse } from '../services/errorService';
import * as Sentry from '@sentry/react';
import RichTextEditor from '../components/shared/RichTextEditor';
import { SourceTextForGenerating } from '../components/SocialMediaPosts/SourceTextForGenerating';
import InputMethodSelector, { InputMethod } from '../components/shared/InputMethodSelector';
import { FeatureFlag } from '../utils/featureFlags';
import FeatureFlagOverlay from '../components/shared/FeatureFlagOverlay';

const SocialMediaPosts: React.FC = () => {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const { domains, targetCompany, customerTargetGroups, fetchRelatedDomainData, fetchDomains } =
    useData();
  const [activeDomain, setActiveDomain] = useState<Domain | null>(null);
  const [inputMethod, setInputMethod] = useState<InputMethod>('url');
  const [articleUrl, setArticleUrl] = useState('');
  const [sourceText, setSourceText] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState('');
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [streamingPosts, setStreamingPosts] = useState<Record<number, string>>({
    0: '',
    1: '',
    2: '',
  });
  const [streamingStatus, setStreamingStatus] = useState<Record<number, string>>({
    0: 'waiting',
    1: 'waiting',
    2: 'waiting',
  });
  const [generatedPosts, setGeneratedPosts] = useState<Post[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    loadData();
  }, [domains, targetCompany]);

  const loadData = () => {
    if (targetCompany && domains.length > 0) {
      // Automatically select the first domain if available
      const foundDomain = domains.find((domain: Domain) => domain.company_id === targetCompany);
      if (foundDomain && typeof foundDomain !== 'undefined') {
        setActiveDomain(foundDomain);
        fetchRelatedDomainData(foundDomain.id);
      } else {
        setActiveDomain(null);
      }
    }
  };

  const generateSinglePost = async (index: number) => {
    try {
      const trimmedUrl = articleUrl.trim();

      const { data: response, error: generateSocialPostsError } =
        await aiService.generateSocialPosts({
          platform: selectedPlatform || '',
          hasUrl: inputMethod === 'url',
          articleUrl: inputMethod === 'url' ? trimmedUrl : '',
          sourceText: inputMethod === 'text' ? sourceText : undefined,
          language: activeDomain?.language || 'English',
          toneOfVoice: activeDomain?.tone_of_voice || 'Professional',
          customerGroup: {
            name: selectedCustomerGroup,
            description:
              customerTargetGroups.find((group) => group.name === selectedCustomerGroup)
                ?.description || '',
          },
          companyId: targetCompany || '',
          domainName: activeDomain?.domain || '',
          customPrompt: customPrompt,
        });
      if (generateSocialPostsError) {
        throw new Error(await extractErrorMessageFromResponse(generateSocialPostsError, t));
      }

      if (!response.body) {
        showNotification('Error generating post', 'error');
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      let content = '';

      const reader = response.body.getReader();
      setStreamingStatus((prev) => ({ ...prev, [index]: 'generating' }));

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (data.content) {
                  content += data.content;
                  setStreamingPosts((prev) => ({
                    ...prev,
                    [index]: content,
                  }));
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      setStreamingStatus((prev) => ({ ...prev, [index]: 'completed' }));
      return content;
    } catch (error) {
      Sentry.captureException(error);
      showNotification(
        error instanceof Error ? error.message : 'Error generating content. Please try again.',
        'error'
      );
      console.error(`Error generating post ${index}:`, error);
      setStreamingStatus((prev) => ({ ...prev, [index]: 'error' }));
      throw error;
    }
  };

  const handleSubmit = async () => {
    const isUrlValid = inputMethod === 'url' && articleUrl.trim();
    const isTextValid = inputMethod === 'text' && sourceText.trim();

    if (!selectedPlatform || !selectedCustomerGroup || (!isUrlValid && !isTextValid)) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setIsGenerating(true);
    setGeneratedPosts([]);
    setStreamingPosts({ 0: '', 1: '', 2: '' });
    setStreamingStatus({ 0: 'waiting', 1: 'waiting', 2: 'waiting' });

    try {
      // Generate all three posts concurrently
      const posts = await Promise.all([
        generateSinglePost(0),
        generateSinglePost(1),
        generateSinglePost(2),
      ]);

      // Convert the generated content into Post objects
      const formattedPosts: Post[] = posts.map((content) => ({
        content,
        hashtags: [], // Add hashtags if needed
        metrics: [], // Add metrics if needed
      }));

      setGeneratedPosts(formattedPosts);
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error generating posts:', error);
      showNotification('Error generating posts. Please try again.', 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  // // Prevent rendering until activeDomain is set
  if (!targetCompany || !activeDomain) {
    return <StatusMessage targetCompany={targetCompany} activeDomain={activeDomain?.id || null} />;
  }

  return (
    <FeatureFlagOverlay feature={FeatureFlag.SOCIAL_MEDIA_POSTS}>
      <div className="p-8">
        <ErrorBoundary>
          <h1 className="text-3xl font-bold mb-2">{t('social.social_media_posts')}</h1>
          <div className="mb-6 mt-4">
            <DomainSelector activeDomain={activeDomain} setActiveDomain={setActiveDomain} />
          </div>

          <ErrorBoundary>
            <div className="mb-6">
              <SocialPlatformSelector
                selectedPlatform={selectedPlatform}
                onSelect={setSelectedPlatform}
              />
            </div>
          </ErrorBoundary>

          <ErrorBoundary>
            <div className="mb-6">
              <CustomerGroupSelector
                customerGroups={customerTargetGroups || []}
                selectedGroup={selectedCustomerGroup}
                onSelect={setSelectedCustomerGroup}
              />
            </div>
          </ErrorBoundary>

          <div className="mb-6">
            <InputMethodSelector inputMethod={inputMethod} onInputMethodChange={setInputMethod} />
          </div>

          {inputMethod === 'url' && (
            <ErrorBoundary>
              <div className="mb-6">
                <ArticleUrlInput
                  value={articleUrl}
                  onChange={(url) => setArticleUrl(Array.isArray(url) ? url[0] || '' : url)}
                />
              </div>
            </ErrorBoundary>
          )}

          {inputMethod === 'text' && (
            <ErrorBoundary>
              <div className="mb-6">
                <SourceTextForGenerating value={sourceText} onChange={setSourceText} />
              </div>
            </ErrorBoundary>
          )}

          <ErrorBoundary>
            <RichTextEditor
              label={t('common.custom_prompt')}
              value={customPrompt}
              onChange={setCustomPrompt}
              simpleText={true}
            />
          </ErrorBoundary>

          <section>
            <button
              onClick={handleSubmit}
              disabled={isGenerating || !selectedCustomerGroup}
              className="bg-blue-500 text-white px-6 py-2 rounded-md hover:bg-blue-600 focus:outline-none disabled:bg-gray-400"
            >
              {isGenerating ? t('social.generating') : t('social.generate_posts')}
            </button>

            {isGenerating && (
              <div className="mt-4 flex items-center">
                <span className="material-symbols-outlined animate-spin text-4xl mr-2">sync</span>
                <p className="text-gray-700 dark:text-gray-200">{t('social.generating_posts')}</p>
              </div>
            )}
          </section>

          {generatedPosts.length > 0 && (
            <ErrorBoundary>
              <GeneratedPosts posts={generatedPosts} platform={selectedPlatform || ''} />
            </ErrorBoundary>
          )}

          {isGenerating && (
            <ErrorBoundary>
              <StreamingModal
                title={t('common.generating_social_posts')}
                items={[0, 1, 2].map(
                  (index): StreamingProgressItem => ({
                    id: index,
                    title: `Post ${index + 1}`,
                    status: streamingStatus[index] || 'waiting',
                    content: streamingPosts[index],
                  })
                )}
                variant="grid"
                showContent={true}
              />
            </ErrorBoundary>
          )}
        </ErrorBoundary>
      </div>
    </FeatureFlagOverlay>
  );
};

export default SocialMediaPosts;
